export interface User {
  id: number;
  user_name: string;
  is_active: boolean;
  last_login?: string;
  role?: string;
  permissions?: string[];
}

export interface Role {
  id: number;
  role_name: string;
  description: string;
  is_system: boolean;
  permissions: { entity: string; action: string }[];
}

export interface AdminUser {
  id: number;
  user_name: string;
  is_active: boolean;
  last_login?: string;
  role_id?: number;
  role_name?: string;
}

export interface AdminSession {
  id: string;
  token_type: 'staff' | 'client_portal' | string;
  user_id?: number | null;
  client_id?: number | null;
  display_name: string;
  login_identifier?: string;
  role_name?: string;
  user_agent?: string;
  ip_address?: string;
  is_active: boolean;
  is_current?: boolean;
  close_reason?: string;
  created_at: string;
  last_seen_at: string;
  closed_at?: string | null;
}

export interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  token: string | null;
}

export interface ClientPortalUser {
  id: number;
  client_name: string;
  telephone: string;
  email: string;
  wilaya: string;
  ville: string;
  must_change_password?: boolean;
}

export interface ClientPortalAuthState {
  isAuthenticated: boolean;
  user: ClientPortalUser | null;
  token: string | null;
}

export interface ClientPortalProfile {
  id: number;
  client_name: string;
  telephone: string;
  email: string;
  wilaya: string;
  ville: string;
  active_contract_count: number;
  total_active_licenses?: number;
  license_counts?: ClientPortalLicenseCount[];
  next_contract_expiry?: string | null;
  must_change_password?: boolean;
}

export interface ClientPortalNotificationSummary {
  chat_unread_count: number;
  ticket_unread_count: number;
  support_online?: boolean;
}

export interface ClientPortalLicenseCount {
  license_type: string;
  license_count: number;
}

export interface ClientPortalTicket {
  id: number;
  subject: string;
  message_text: string;
  status: string;
  client_email: string;
  created_at: string;
  updated_at: string;
  unread_reply_count?: number;
  attachments?: ClientPortalTicketAttachment[];
  messages?: SupportTicketMessage[];
}

export interface ClientPortalTicketAttachment {
  id: number;
  file_name: string;
  content_type: string;
  file_size: number;
  created_at: string;
}

export interface SupportTicketMessage {
  id: number;
  sender_type: 'client' | 'staff';
  staff_id?: number | null;
  staff_name?: string | null;
  message_text: string;
  read_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface SupportChatMessage {
  id: number;
  sender_type: 'client' | 'staff';
  message_type: string;
  staff_id?: number | null;
  staff_name?: string | null;
  message_text: string;
  read_at?: string | null;
  created_at: string;
  updated_at: string;
  attachments?: SupportChatAttachment[];
}

export interface SupportChatAttachment {
  id: number;
  file_name: string;
  content_type: string;
  file_size: number;
  created_at: string;
}

export interface SupportChatCallSignal {
  conversation_id: number;
  client_id: number;
  call_status: 'idle' | 'ringing' | 'active';
  action?: string;
  initiator_type?: 'client' | 'staff';
  initiator_staff_id?: number | null;
  initiator_name?: string | null;
  assigned_staff_id?: number | null;
  assigned_staff_name?: string | null;
  actor_type?: 'client' | 'staff' | 'system';
  actor_name?: string | null;
  reason?: string | null;
  updated_at?: string | null;
}

export interface SupportChatCallLog {
  id: number;
  status: 'ringing' | 'active' | 'ended' | string;
  outcome?: string | null;
  initiator_type: 'client' | 'staff';
  initiator_staff_id?: number | null;
  initiator_name?: string | null;
  assigned_staff_id?: number | null;
  assigned_staff_name?: string | null;
  requested_at: string;
  accepted_at?: string | null;
  ended_at?: string | null;
  duration_seconds?: number;
  updated_at?: string | null;
  created_at: string;
}

export interface SupportChatConversationSummary {
  id: number;
  client_id: number;
  client_name: string;
  telephone: string;
  client_email: string;
  assigned_staff_id?: number | null;
  assigned_staff_name?: string | null;
  assigned_at?: string | null;
  last_message_at?: string | null;
  created_at: string;
  updated_at: string;
  status?: string;
  closed_at?: string | null;
  last_message_text: string;
  unread_count: number;
}

export interface SupportChatConversationDetail extends SupportChatConversationSummary {
  closed_by_staff_id?: number | null;
  closed_by_staff_name?: string | null;
  messages?: SupportChatMessage[];
  call_logs?: SupportChatCallLog[];
}

export interface ClientPortalChatConversation {
  id: number;
  client_id: number;
  assigned_staff_id?: number | null;
  assigned_staff_name?: string | null;
  assigned_at?: string | null;
  last_message_at?: string | null;
  created_at: string;
  updated_at: string;
  status?: string;
  closed_at?: string | null;
  messages?: SupportChatMessage[];
  call_logs?: SupportChatCallLog[];
}

export interface ClientPortalChatUpdateSignal {
  changed: boolean;
  conversation_changed: boolean;
  conversation_updated_at?: string | null;
  support_online: boolean;
  timed_out: boolean;
}

export interface SupportChatUpdateSignal {
  list_changed: boolean;
  conversation_changed: boolean;
  conversation_exists: boolean;
  list_count: number;
  list_state?: string | null;
  conversation_state?: string | null;
  timed_out: boolean;
}

export interface SupportStaffUser {
  id: number;
  user_name: string;
  role_name?: string;
}

export interface SupportTicketSummary {
  id: number;
  client_id: number;
  client_name: string;
  telephone: string;
  subject: string;
  status: string;
  client_email: string;
  created_at: string;
  updated_at: string;
  assigned_staff_id?: number | null;
  assigned_staff_name?: string | null;
  assigned_at?: string | null;
  treated_by_staff_id?: number | null;
  treated_by_staff_name?: string | null;
  treated_at?: string | null;
  archived_at?: string | null;
  attachment_count: number;
}

export interface SupportTicketDetail extends SupportTicketSummary {
  wilaya: string;
  ville: string;
  message_text: string;
  assigned_by_staff_id?: number | null;
  assigned_by_staff_name?: string | null;
  attachments?: ClientPortalTicketAttachment[];
  messages?: SupportTicketMessage[];
}

export interface Contrat {
  id: number;
  id_client: number;
  client_name: string;
  telephone?: string;
  date_debut: string;
  date_fin: string;
  t_contrat: string;
  mode_reglt: string;
}

export interface Client {
  id: number;
  client_name: string;
  telephone: string;
  email: string;
  wilaya: string;
  ville: string;
  has_contrats?: boolean;
  has_active_non_expired_contrat?: boolean;
}

// Dernier envoi d'un client vers les tiers du remote-server (Remote Desktop).
// tiers_id / envoye_le : dernier envoi accepte ; dernier_* / tente_le : dernier
// essai, refuse ou non. Dates en heure d'Alger, "AAAA-MM-JJTHH:MM:SS".
export interface ClientRemoteTiers {
  client_id: number;
  tiers_id: string | null;
  archive: boolean;
  envoye_le: string | null;
  dernier_resultat: string;
  derniere_erreur: string | null;
  dernier_champ: string | null;
  tente_le: string;
}

export interface ClientRemoteTiersEtat {
  // Cle [RemoteDesktop] Api_Key posee dans config.ini
  configure: boolean;
  // Migration create_clients_remote_tiers.sql appliquee
  disponible: boolean;
  envois: ClientRemoteTiers[];
}

export type ClientRemoteTiersResultatCode =
  | 'cree'
  | 'adopte'
  | 'mis_a_jour'
  | 'inchange'
  | 'refuse'
  | 'introuvable';

export interface ClientRemoteTiersResultat {
  client_id: number;
  client_name: string;
  resultat: ClientRemoteTiersResultatCode;
  tiers_id?: string;
  archive?: boolean;
  error?: string;
  field?: string | null;
}

export interface ClientRemoteTiersEnvoi {
  resultats: ClientRemoteTiersResultat[];
  acceptes: number;
  refuses: number;
  introuvables: number;
}

export interface License {
  id: number;
  client_id: number;
  client_name: string;
  hardware_id: string;
  activation_key: string;
  license_type: string;
  created_at: string;
  expires_at: string;
  is_active: boolean;
  notes: string;
}

export interface CnrcDesktopLicense {
  id: number;
  license_key: string;
  status: 'free' | 'used' | string;
  created_at?: string;
  created_by?: string;
  machine_hash?: string;
  machine_code?: string;
  pc_name?: string;
  windows_user?: string;
  app_version?: string;
  ip_address?: string;
  activated_at?: string;
  last_check_at?: string;
}

// Catalogue des versions des applications de bureau (page Gestion des versions)
export type DesktopProduit = 'SCF' | 'GSCOM' | 'PAIE' | 'SVC' | 'RSCF' | 'RGSCOM' | 'RPAIE' | 'CHEQUE';

export interface DesktopVersion {
  id: number;
  produit: DesktopProduit;
  version: string;
  publiee_le: string | null;
  taille_octets: number | null;
  obligatoire: boolean;
  notes: string | null;
  publiee: boolean;
  derniere_publiee: boolean;
  // Palier 2 : le lot. Les trois lot_* sont remplies ensemble ou pas du tout.
  lot_fichier: string | null;
  lot_taille_octets: number | null;
  lot_sha256: string | null;
  porteur_fichier: string | null;
  // Ajoutés par le serveur à l'affichage (état du dépôt, hors base) :
  // null = aucun fichier rattaché.
  lot_fichier_ok: boolean | null;
  lot_url: string | null;
  porteur_fichier_ok: boolean | null;
  porteur_url: string | null;
  cree_le: string;
}

// Objet telechargement rendu au poste couvert (contrat du palier 2). Absent —
// et non pas vide — quand le poste n'y a pas droit ou qu'il n'y a pas de lot.
export interface DesktopTelechargement {
  url: string;
  taille_octets: number;
  sha256: string;
}

// « oui » : le poste couvert reçoit l'objet ; « fichier_absent » : un lot est
// rattaché mais son fichier manque du dépôt ou a changé de taille ; « non » :
// aucun lot rattaché (ou rien à télécharger).
export type DesktopLotEtat = 'oui' | 'non' | 'fichier_absent';

// Un fichier présent dans le dépôt d'un produit (storage/maj/<produit>)
export interface DesktopDepotFichier {
  nom: string;
  taille_octets: number;
  depose_le: string;
  rattache_a: string | null;
}

export interface DesktopLotInput {
  lot_fichier: string;
  porteur_fichier?: string | null;
}

export interface DesktopVersionInput {
  produit: DesktopProduit;
  version: string;
  publiee_le: string | null;
  taille_octets: number | null;
  obligatoire: boolean;
  notes: string | null;
}

// Objet mise_a_jour renvoye aux postes (contrat du palier 1)
export interface DesktopMiseAJour {
  disponible: boolean;
  derniere_version: string | null;
  publiee_le: string | null;
  taille_octets: number | null;
  obligatoire: boolean;
  notes: string | null;
}

// Accès distant : abonnements DDNS des serveurs de clients (page Accès distant)
export type DdnsEtat = 'ACTIF' | 'EXPIRE' | 'SUSPENDU';

export interface DdnsServeur {
  id: number;
  id_client: number;
  client_name: string;
  nom_serveur: string;
  ip: string | null;
  service_version: string | null;
  date_debut: string;
  date_expiration: string;
  jours_restants: number;
  expiration_si_renouvelee: string;
  etat: DdnsEtat;
  actif: boolean;
  en_ligne: boolean;
  vu_le: string | null;
  ip_changee_le: string | null;
  notes: string | null;
  cree_le: string;
  // Jetons et postes (politique du 03/10/2026) : en vigueur seulement
  jetons_actifs: number;
  postes_autorises: number;
  postes_en_attente: number;
}

// Jetons d'enrôlement des postes distants et postes enrôlés (volet « Postes » de la page Accès
// distant). Ni jeton, ni secret, ni empreinte : le jeton n'est rendu qu'à sa création.
export type DdnsModeEnrolement = 'APPROBATION' | 'OUVERT' | 'FERME';
export type DdnsEtatJeton = 'ACTIF' | 'ECHU' | 'REVOQUE';
export type DdnsStatutPoste = 'EN_ATTENTE' | 'AUTORISE' | 'REFUSE' | 'REVOQUE';

export interface DdnsJetonPostes {
  id: number;
  id_serveur: number;
  libelle: string;
  etat: DdnsEtatJeton;
  mode_enrolement: DdnsModeEnrolement;
  postes_max: number | null;
  expire_le: string | null;
  direct_jusqu_au: string | null;
  direct_tolere: boolean;
  postes_autorises: number;
  postes_en_attente: number;
  cree_le: string;
  cree_par: string | null;
  derniere_utilisation: string | null;
  revoque_le: string | null;
  revoque_par: string | null;
  motif_revocation: string | null;
}

export interface DdnsJetonPostesInput {
  libelle: string;
  mode_enrolement: DdnsModeEnrolement;
  postes_max: number | null;
  expire_le: string | null;
  direct_jusqu_au: string | null;
}

export interface DdnsPoste {
  id: number;
  code: string;
  id_jeton: number;
  jeton_libelle: string;
  statut: DdnsStatutPoste;
  libelle: string | null;
  hardware_id: string;
  nom_machine: string | null;
  produit: string;
  app_version: string | null;
  ip_enrolement: string;
  enrole_le: string;
  decide_le: string | null;
  decide_par: string | null;
  motif_decision: string | null;
  vu_le: string | null;
  derniere_ip: string | null;
  en_ligne: boolean;
  doublons_materiel: number;
}

export interface DdnsServeurInput {
  id_client: number;
  nom_serveur: string;
  date_debut: string;
  date_expiration: string;
  notes: string | null;
}

// Suivi des créances (page Créances), en deux étapes : constatation, puis règlement.
// Le règlement porte sur le compte du client : solde = créances − règlements (> 0 : le client
// doit ; < 0 : il a versé d'avance). Montants : nombres JSON à 2 décimales, calculés par le
// serveur en NUMERIC ; côté page, pour l'affichage seulement.
export interface Creance {
  id: number;
  id_client: number;
  client_name: string;
  date_creance: string;
  montant: number;
  observation: string | null;
  saisi_par: string | null;
  cree_le: string;
}

export interface CreanceInput {
  id_client: number;
  date_creance: string;
  /** Texte décimal exact, point décimal : « 15000.50 » (voir normaliserMontant). */
  montant: string;
  observation: string | null;
}

export interface Reglement {
  id: number;
  id_client: number;
  client_name: string;
  date_reglement: string;
  montant: number;
  saisi_par: string | null;
  cree_le: string;
}

export interface ReglementInput {
  id_client: number;
  date_reglement: string;
  montant: string;
}

export interface CreanceSolde {
  id_client: number;
  client_name: string;
  total_creances: number;
  total_reglements: number;
  solde: number;
  nb_creances: number;
  nb_reglements: number;
  derniere_operation: string;
  /** Première créance que les règlements ne couvrent pas en entier (imputés aux plus anciennes). */
  plus_ancienne_impayee: string | null;
  anciennete_jours: number | null;
}

export interface CreancesTotaux {
  total_creances: number;
  total_reglements: number;
  solde: number;
}

export interface ReleveLigne {
  nature: 'CREANCE' | 'REGLEMENT';
  id: number;
  date_operation: string;
  debit: number;
  credit: number;
  /** Solde du compte après cette ligne, mouvements antérieurs à la période compris. */
  solde: number;
  observation: string | null;
  saisi_par: string | null;
}

export interface ReleveClient {
  client: {
    id: number;
    client_name: string;
    telephone: string | null;
    email: string | null;
    wilaya: string | null;
    ville: string | null;
  };
  du: string | null;
  au: string | null;
  solde_initial: number;
  total_debit: number;
  total_credit: number;
  solde_final: number;
  lignes: ReleveLigne[];
}

export interface Phone {
  phone_number: string;
}

export interface AuditEntry {
  id: number;
  table_name: string;
  record_id: number;
  operation: 'INSERT' | 'UPDATE' | 'DELETE';
  old_values: Record<string, any> | null;
  new_values: Record<string, any> | null;
  user_name: string;
  created_at: string;
}

export const WILAYAS = [
  'Adrar', 'Chlef', 'Laghouat', 'Oum El Bouaghi', 'Batna', 'Béjaïa', 'Biskra',
  'Béchar', 'Blida', 'Bouira', 'Tamanrasset', 'Tébessa', 'Tlemcen', 'Tiaret',
  'Tizi Ouzou', 'Alger', 'Djelfa', 'Jijel', 'Sétif', 'Saïda', 'Skikda',
  'Sidi Bel Abbès', 'Annaba', 'Guelma', 'Constantine', 'Médéa', 'Mostaganem',
  'M\'Sila', 'Mascara', 'Ouargla', 'Oran', 'El Bayadh', 'Illizi', 'Bordj Bou Arréridj',
  'Boumerdès', 'El Tarf', 'Tindouf', 'Tissemsilt', 'El Oued', 'Khenchela',
  'Souk Ahras', 'Tipaza', 'Mila', 'Aïn Defla', 'Naâma', 'Aïn Témouchent',
  'Ghardaïa', 'Relizane', 'Timimoun', 'Bordj Badji Mokhtar', 'Ouled Djellal',
  'Béni Abbès', 'In Salah', 'In Guezzam', 'Touggourt', 'Djanet', 'El M\'Ghair', 'El Meniaa',
  'Aflou', 'Barika', 'El Kantara', 'Bir El Ater', 'El Aricha',
  'Ksar Chellala', 'Aïn Oussara', 'Messaâd', 'Ksar El Boukhari', 'Boussaâda',
  'El Bayadh Sidi Cheikh'
];

export const CONTRACT_TYPES = [
  'Instance de paiement',
  'Actif',
  'Bloqué',
  'Instance de confirmation'
];

export const PAYMENT_MODES = [
  'Cheque',
  'Virment',
  'Instance',
  'Espece',
  'C.C.P.',
  'Nouvelle inst.'
];

export const LICENSE_TYPES = [
  { value: 'WINALCO PAIE', label: 'WINALCO PAIE' },
  { value: 'WINALCO GSCOM', label: 'WINALCO GSCOM' },
  { value: 'WINALCO SCF', label: 'WINALCO SCF' },
  { value: 'WINALCO CHEQUE', label: 'WINALCO CHEQUE' },
];
